import {
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { addFileToQueue, createDistributor, detectVendors } from '../../Actions';
import { fingerprintAll } from './fingerprint';

// Server caps the detection request at 200 files.
const MAX_FILES = 200;
// Concurrent uploads. The upload endpoint streams straight to S3 one file per request; a few in
// flight saturates the link without stampeding a t3.small.
const UPLOAD_CONCURRENCY = 3;

const ACCEPT = {
  'application/pdf': ['.pdf'],
  'text/csv': ['.csv'],
  'application/vnd.ms-excel': ['.xls', '.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/webp': ['.webp'],
};
const ACCEPTED_EXT_RE = /\.(pdf|csv|xls|xlsx|png|jpe?g|webp)$/i;

const TIER_LABEL = {
  auto: { label: 'Confident', color: 'primary' },
  suggest: { label: 'Likely', color: 'default' },
  unknown: { label: 'Needs review', color: 'secondary' },
};
const tierLabel = (tier) => TIER_LABEL[tier] || TIER_LABEL.unknown;

const pct = (confidence) => `${Math.round((confidence || 0) * 100)}%`;

// A folder drop hands us every file in the tree, including .DS_Store, Thumbs.db and whatever else.
// react-dropzone's `accept` filters the picker, not a traversed directory, so filter again here.
const keepUploadable = (files) => files.filter((f) => ACCEPTED_EXT_RE.test(f.name) && f.size > 0);

export default function BulkUpload({ open, onClose, distributorList, onUploaded, onNotify }) {
  const [phase, setPhase] = useState('idle'); // idle | scanning | review | uploading | done
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const folderInputRef = useRef(null);

  const vendorOptions = useMemo(() => {
    const existing = (distributorList || []).map((d) => d.distributor_name);
    const proposed = (summary?.proposedVendors || []).filter((name) => !existing.includes(name));
    return [...existing, ...proposed].sort((a, b) => a.localeCompare(b));
  }, [distributorList, summary]);

  const reset = () => {
    setPhase('idle');
    setRows([]);
    setSummary(null);
    setProgress({ done: 0, total: 0 });
  };

  const handleClose = () => {
    if (phase === 'uploading') return; // never abandon files mid-queue
    reset();
    onClose();
  };

  const scan = useCallback(async (incoming) => {
    const files = keepUploadable(incoming);
    if (!files.length) {
      onNotify('warning', 'No supported files found. Accepted: PDF, CSV, XLS, XLSX, PNG, JPG, WEBP.');
      return;
    }
    const capped = files.slice(0, MAX_FILES);
    if (files.length > MAX_FILES) {
      onNotify('warning', `Only the first ${MAX_FILES} files will be processed (${files.length} were selected).`);
    }

    setPhase('scanning');
    setProgress({ done: 0, total: capped.length });

    const fingerprints = await fingerprintAll(capped, {
      onProgress: (done, total) => setProgress({ done, total }),
    });

    const detected = await detectVendors(fingerprints)
      .then((r) => r.data)
      .catch(() => null);

    if (!detected) {
      setPhase('idle');
      onNotify('error', 'Could not analyze those files. Please try again.');
      return;
    }

    const byKey = new Map(detected.files.map((d) => [d.key, d]));
    setRows(capped.map((file, index) => {
      const detection = byKey.get(fingerprints[index].key) || {};
      // Only auto/suggest pre-fill the vendor. 'unknown' is left blank on purpose: a wrong vendor
      // writes this file's costs against the wrong distributor.
      const guess = detection.guess;
      return {
        key: fingerprints[index].key,
        file,
        fileName: file.name,
        folderName: fingerprints[index].folderName,
        tier: detection.tier || 'unknown',
        engine: detection.engine || 'ai',
        confidence: guess?.confidence,
        reasons: (guess || detection.topCandidate)?.reasons || [],
        hint: !guess && detection.topCandidate ? detection.topCandidate : null,
        vendorName: guess?.distributorName || '',
        status: 'pending',
        error: null,
      };
    }));
    setSummary(detected.summary);
    setPhase('review');
  }, [onNotify]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    accept: ACCEPT,
    noClick: phase !== 'idle',
    onDrop: scan,
  });

  const setVendor = (key, vendorName) => {
    setRows((prior) => prior.map((row) => (row.key === key ? { ...row, vendorName } : row)));
  };

  const patchRow = (key, patch) => {
    setRows((prior) => prior.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const unresolvedCount = rows.filter((r) => !r.vendorName?.trim()).length;

  const submit = async () => {
    setPhase('uploading');

    // Create every not-yet-existing vendor ONCE, up front, and upload with a real distributorUUID.
    // Letting the upload endpoint create them would race: N concurrent uploads of one new vendor.
    const known = new Map((distributorList || []).map((d) => [d.distributor_name, d.distributor_uuid]));
    const needed = [...new Set(rows.map((r) => r.vendorName.trim()).filter((name) => name && !known.has(name)))];

    for (const name of needed) {
      const created = await createDistributor(name).then((r) => r.data).catch(() => null);
      if (!created?.distributorUuid) {
        setPhase('review');
        onNotify('error', `Could not create vendor "${name}".`);
        return;
      }
      known.set(name, created.distributorUuid);
      // The server sanitizes the name; keep the client's map keyed on what it actually stored.
      known.set(created.distributorName, created.distributorUuid);
    }

    const queue = [...rows];
    let creditsExhausted = false;

    const worker = async () => {
      for (let row = queue.shift(); row; row = queue.shift()) {
        if (creditsExhausted) {
          patchRow(row.key, { status: 'skipped', error: 'Credit limit reached' });
          continue;
        }
        patchRow(row.key, { status: 'uploading' });

        const name = row.vendorName.trim();
        const formData = new FormData();
        formData.append('dataFile', row.file);

        const res = await addFileToQueue(formData, name, known.get(name) ?? '', row.fileName)
          .catch((err) => err?.response ?? null);

        if (res?.status === 409 && res?.data?.engine === 'mapping') {
          // A JSON/SQL-mapped vendor runs the interactive legacy engine, which cannot be batched.
          patchRow(row.key, { status: 'mapping', error: 'Has a mapping — upload this one individually' });
        } else if (res?.status === 402) {
          creditsExhausted = true;
          patchRow(row.key, { status: 'skipped', error: 'Credit limit reached' });
        } else if (!res || res.status >= 400) {
          patchRow(row.key, { status: 'failed', error: res?.data?.message || 'Upload failed' });
        } else {
          patchRow(row.key, { status: 'queued', error: null });
        }
      }
    };

    await Promise.all(Array.from({ length: Math.min(UPLOAD_CONCURRENCY, rows.length) }, worker));

    setPhase('done');
    if (creditsExhausted) onNotify('warning', 'Monthly AI credit limit reached. Remaining files were not queued.');
    onUploaded();
  };

  const statusChip = (row) => {
    if (row.status === 'queued') return <Chip size="small" label="Queued" style={{ backgroundColor: '#e8f5e9' }} />;
    if (row.status === 'uploading') return <CircularProgress size={16} />;
    if (row.status === 'failed') return <Chip size="small" color="secondary" label={row.error} />;
    if (row.status === 'mapping') return <Tooltip title={row.error}><Chip size="small" label="Use mapping engine" /></Tooltip>;
    if (row.status === 'skipped') return <Chip size="small" label={row.error} />;
    return null;
  };

  const aiCount = summary?.aiFileCount ?? 0;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="lg">
      <DialogTitle>Bulk upload</DialogTitle>
      <DialogContent>
        {phase === 'idle' && (
          <>
            <div
              {...getRootProps()}
              style={{
                border: `2px dashed ${isDragActive ? '#142e3e' : '#aaa'}`,
                borderRadius: 4,
                padding: '32px 16px',
                textAlign: 'center',
                cursor: 'pointer',
                background: isDragActive ? '#e3f2fd' : 'transparent',
              }}
            >
              <input {...getInputProps()} />
              <Typography variant="body1" color={isDragActive ? 'primary' : 'textSecondary'}>
                {isDragActive
                  ? 'Drop files or a folder here...'
                  : 'Drag and drop files — or a whole folder — here, or click to select. Accepted: PDF, CSV, XLS, XLSX, PNG, JPG, WEBP.'}
              </Typography>
            </div>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              {/* Dropping a folder works via traversal, but the OS "browse" dialog needs an input
                  explicitly marked webkitdirectory to offer folder selection. */}
              <input
                ref={folderInputRef}
                type="file"
                webkitdirectory=""
                directory=""
                multiple
                style={{ display: 'none' }}
                onChange={(e) => scan(Array.from(e.target.files || []))}
              />
              <Button variant="outlined" onClick={() => folderInputRef.current?.click()}>
                Select a folder
              </Button>
              <Typography variant="caption" display="block" color="textSecondary" style={{ marginTop: 8 }}>
                We&apos;ll try to identify the vendor for each file. You confirm before anything is queued.
              </Typography>
            </div>
          </>
        )}

        {phase === 'scanning' && (
          <div style={{ padding: '32px 8px' }}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Identifying vendors — {progress.done} of {progress.total} files
            </Typography>
            <LinearProgress
              variant={progress.total ? 'determinate' : 'indeterminate'}
              value={progress.total ? (progress.done / progress.total) * 100 : 0}
            />
          </div>
        )}

        {(phase === 'review' || phase === 'uploading' || phase === 'done') && (
          <>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
              {rows.length} files · {summary?.auto ?? 0} confident · {summary?.suggest ?? 0} likely ·{' '}
              {summary?.unknown ?? 0} need review
              {aiCount > 0 && ` · ${aiCount} will use AI credits`}
            </Typography>
            {unresolvedCount > 0 && phase === 'review' && (
              <Typography variant="body2" color="error" style={{ marginBottom: 8 }}>
                {unresolvedCount} file{unresolvedCount === 1 ? '' : 's'} still need a vendor. Nothing is uploaded until
                every file has one.
              </Typography>
            )}
            <TableContainer component={Paper} variant="outlined" style={{ maxHeight: 420 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>File</TableCell>
                    <TableCell style={{ width: 260 }}>Vendor</TableCell>
                    <TableCell style={{ width: 130 }}>Confidence</TableCell>
                    <TableCell>Why</TableCell>
                    <TableCell style={{ width: 160 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.key}>
                      <TableCell>
                        <Typography variant="body2">{row.fileName}</Typography>
                        {row.folderName && (
                          <Typography variant="caption" color="textSecondary">{row.folderName}/</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Autocomplete
                          freeSolo
                          size="small"
                          disabled={phase !== 'review'}
                          options={vendorOptions}
                          value={row.vendorName}
                          onChange={(_, value) => setVendor(row.key, value || '')}
                          onInputChange={(_, value) => setVendor(row.key, value || '')}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder={row.hint ? `e.g. ${row.hint.distributorName}?` : 'Select or type'}
                              error={phase === 'review' && !row.vendorName?.trim()}
                            />
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={tierLabel(row.tier).color}
                          label={row.confidence
                            ? `${tierLabel(row.tier).label} ${pct(row.confidence)}`
                            : tierLabel(row.tier).label}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="textSecondary">
                          {row.reasons[0]
                            || (row.hint ? `Closest match: ${row.hint.distributorName}` : 'No vendor signals found')}
                        </Typography>
                      </TableCell>
                      <TableCell>{statusChip(row)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" style={{ margin: 10 }} onClick={handleClose} disabled={phase === 'uploading'}>
          {phase === 'done' ? 'Close' : 'Cancel'}
        </Button>
        {phase === 'review' && (
          <Button
            variant="contained"
            style={{ backgroundColor: '#142e3e', color: '#28c4fc', margin: 10 }}
            disabled={!rows.length || unresolvedCount > 0}
            onClick={submit}
          >
            Upload {rows.length} file{rows.length === 1 ? '' : 's'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
